import React, { useState, useEffect } from "react";
import T from '../../../components/common/T';
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { usePurchaseOrder } from "../../../context/PurchaseOrderContext";
import { useAccounting } from "../../../context/AccountingContext"; // 🟢 Import Accounting Context
import OdooFormLayout from "../../../components/OdooFormLayout"; // Keeping your layout
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Price from "../../../components/common/Price";
export default function VendorBill() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const poId = searchParams.get("poId");
  
  // Contexts
  const { purchaseOrders } = usePurchaseOrder();
  const { createVendorBill } = useAccounting(); // 🟢 Use the Accounting Function

  // State
  const [billData, setBillData] = useState(null);
  const [inputs, setInputs] = useState({
      billNumber: "",
      billDate: new Date().toISOString().split('T')[0],
      dueDate: ""
  });
  const [loading, setLoading] = useState(false);

  // 1. Load Data from PO
  useEffect(() => {
    // Try to get PO from State (passed from List) OR find it in Context
    const po = location.state?.poData || purchaseOrders.find(p => String(p.id) === String(poId));
    
    if (po) {
        setBillData({
            id: po.id,
            vendor: po.vendorName,
            vendorId: po.vendorId,
            projectId: po.projectId, // 🟢 Important for Budget
            lines: [{ 
                product: po.materialName, 
                qty: po.quantity, 
                price: po.totalPrice / (po.quantity || 1), // Estimate unit price if missing
                subtotal: po.totalPrice 
            }],
            total: po.totalPrice,
            status: po.status === "Completed" ? "PAID" : (po.status === "Billed" ? "POSTED" : "DRAFT")
        });

        // Set default due date to 30 days from now
        const due = new Date();
        due.setDate(due.getDate() + 30);
        setInputs(prev => ({ ...prev, dueDate: due.toISOString().split('T')[0] }));
    }
  }, [poId, purchaseOrders, location.state]);

// --- UPDATED HANDLE POST (Sends Line Items) ---
  const handlePost = async () => {
      // Validation
      if (!billData) return alert("Error: Bill Data is missing!");
      if (!inputs.billNumber) return alert("Please enter the Vendor Invoice Number first!");

      setLoading(true);
      
      try {
          // 🟢 NEW: Construct the Detailed Payload
          const payload = {
              vendor_id: billData.vendorId,
              project_id: billData.projectId,
              purchase_order_id: billData.id,
              bill_number: inputs.billNumber,
              bill_date: inputs.billDate,
              due_date: inputs.dueDate,
              total_amount: billData.total,
              notes: `Generated from PO #${poId}`,
              
              // 🟢 SEND LINES for Cost Sheet
              items: billData.lines.map(line => ({
                  product_name: line.product,
                  quantity: line.qty,
                  unit_price: line.price,
                  total_price: line.subtotal
              }))
          };
          
          await createVendorBill(payload);

          toast.success("Bill Posted & Cost Sheet Updated!");
          
          setBillData(prev => ({ ...prev, status: "POSTED" }));
          setTimeout(() => navigate('/admin/material/purchase-orders'), 1500);

      } catch (err) {
          console.error("API Error:", err);
          toast.error("Error: " + (err.response?.data?.error || err.message));
      } finally {
          setLoading(false);
      }
  };
  if (!billData) return <div className="p-10 text-center text-gray-500"><T>Loading Bill Details...</T></div>;

  // Buttons Configuration
  const ActionButtons = (
    <>
      {billData.status === "DRAFT" && (
          <button 
            onClick={handlePost} 
            disabled={loading}
            className={`bg-purple-700 text-white px-4 py-1.5 rounded text-xs font-bold uppercase shadow-sm ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-800'}`}
          >
              {loading ? "Posting..." : "Post Bill"}
          </button>
      )}
      {billData.status === "POSTED" && (
          <span className="text-green-600 font-bold text-xs uppercase px-3 py-1 bg-green-50 border border-green-200 rounded">
              Verified & Posted
          </span>
      )}
      <button onClick={() => navigate(-1)} className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-50 text-xs font-bold uppercase ml-2">
          Back
      </button>
    </>
  );

  return (
    <>
        <OdooFormLayout
            title={inputs.billNumber ? `BILL / ${inputs.billNumber}` : "Draft Vendor Bill"}
            subTitle={`Vendor: ${billData.vendor}`}
            status={billData.status}
            stages={["DRAFT", "POSTED", "PAID"]}
            actions={ActionButtons}
        >
            {/* PAID RIBBON */}
            {billData.status === "PAID" && (
                <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden z-20 pointer-events-none">
                    <div className="absolute top-3 -right-8 w-32 bg-green-500 text-white text-xs font-bold py-1.5 text-center transform rotate-45 shadow-md">
                        PAID
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-8">
                {/* LEFT COLUMN */}
                <div className="space-y-4">
                    <div className="flex items-center">
                        <label className="w-32 text-xs font-bold text-gray-700 uppercase"><T>Vendor</T></label> 
                        <span className="border-b border-gray-200 flex-1 py-1 text-gray-900 font-medium">{billData.vendor}</span>
                    </div>
                    <div className="flex items-center">
                        <label className="w-32 text-xs font-bold text-gray-700 uppercase"><T>Project</T></label> 
                        <span className="border-b border-gray-200 flex-1 py-1 text-gray-500 italic">
                            {billData.projectId ? `Linked (ID: ${billData.projectId})` : "General Overhead"}
                        </span>
                    </div>
                </div>

                {/* RIGHT COLUMN (Inputs) */}
                <div className="space-y-4">
                    <div className="flex items-center">
                        <label className="w-32 text-xs font-bold text-gray-700 uppercase"><T>Bill Date</T></label> 
                        {billData.status === "DRAFT" ? (
                            <input 
                                type="date" 
                                className="border-b border-gray-300 flex-1 py-1 focus:outline-none focus:border-purple-600 bg-transparent"
                                value={inputs.billDate}
                                onChange={e => setInputs({...inputs, billDate: e.target.value})}
                            />
                        ) : (
                            <span className="border-b flex-1 py-1">{inputs.billDate}</span>
                        )}
                    </div>
                    <div className="flex items-center">
                        <label className="w-32 text-xs font-bold text-gray-700 uppercase"><T>Due Date</T></label> 
                        {billData.status === "DRAFT" ? (
                             <input 
                                type="date" 
                                className="border-b border-gray-300 flex-1 py-1 focus:outline-none focus:border-purple-600 bg-transparent"
                                value={inputs.dueDate}
                                onChange={e => setInputs({...inputs, dueDate: e.target.value})}
                            />
                        ) : (
                            <span className="border-b flex-1 py-1">{inputs.dueDate}</span>
                        )}
                    </div>
                    <div className="flex items-center">
                        <label className="w-32 text-xs font-bold text-purple-700 uppercase"><T>Invoice # </T><span className="text-red-500">*</span></label> 
                        {billData.status === "DRAFT" ? (
                             <input 
                                type="text" 
                                placeholder="e.g. INV-2026-001"
                                className="border-b border-gray-300 flex-1 py-1 focus:outline-none focus:border-purple-600 font-bold bg-purple-50 px-2"
                                value={inputs.billNumber}
                                onChange={e => setInputs({...inputs, billNumber: e.target.value})}
                            />
                        ) : (
                            <span className="border-b flex-1 py-1 font-bold">{inputs.billNumber}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* LINES TABLE */}
            <div className="mt-8">
                <div className="flex border-b border-gray-200 mb-4">
                    <button className="px-6 py-2 text-sm font-bold border-b-2 border-purple-600 text-purple-600"><T>Invoice Lines</T></button>
                </div>
                <table className="w-full text-left text-sm">
                    <thead className="text-gray-500 font-bold border-b bg-gray-50">
                        <tr>
                            <th className="px-3 py-2"><T>Product / Service</T></th>
                            <th className="px-3 py-2 text-right"><T>Quantity</T></th>
                            <th className="px-3 py-2 text-right"><T>Unit Price</T></th>
                            <th className="px-3 py-2 text-right"><T>Subtotal</T></th>
                        </tr>
                    </thead>
                    <tbody>
                        {billData.lines.map((line, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="px-3 py-3 font-medium text-gray-800">{line.product}</td>
                                <td className="px-3 py-3 text-right text-gray-600">{line.qty}</td>
                                <td className="px-3 py-3 text-right text-gray-600"><Price amount={Number(line.price)} /></td>
                                <td className="px-3 py-3 text-right font-bold text-gray-900"><Price amount={Number(line.subtotal)} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="flex justify-end mt-6">
                    <div className="w-72 bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span><T>Untaxed Amount:</T></span>
                            <span><Price amount={(billData.total * 0.82).toFixed(2)} /></span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 mb-2 border-b border-gray-200 pb-2">
                            <span><T>Tax (Est. 18%):</T></span>
                            <span><Price amount={(billData.total * 0.18).toFixed(2)} /></span>
                        </div>
                        <div className="flex justify-between text-xl font-bold text-gray-900 pt-2">
                            <span><T>Total:</T></span>
                            <span><Price amount={Number(billData.total)} /></span>
                        </div>
                    </div>
                </div>
            </div>
        </OdooFormLayout>
    </>
  );
}